import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  CheckCircle,
  BookOpen,
  AlertTriangle,
  Clock,
  Phone,
  ArrowRight,
  Tag,
} from 'lucide-react';

import { api } from '../api';
import { useFetch } from '../hooks/useFetch';
import { useToast } from '../context/ToastContext';
import PageHero from '../components/PageHero';

// ─── RISK SHARED ─────────────────────────────────────
function RiskPage({ courseId, riskNum }) {
  const toast = useToast();
  const { data: course } = useFetch(() => api.getRiskCourse(courseId));
  const [selectedDate, setSelectedDate] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const isR1 = riskNum === 1;

  const c = course || {
    pris: isR1 ? 499 : 1990,
    duration: isR1 ? '3 timmar' : 'Heldag (ca 7h)',
    dates: [],
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDate) {
      toast('Välj ett datum.', 'error');
      return;
    }

    setSubmitting(true);

    try {
      await api.createRiskBooking({
        courseId,
        date: selectedDate,
        ...form,
      });

      setDone(true);
      toast('Bokning bekräftad!', 'success');
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const riskDescription = isR1
    ? 'Riskutbildning Del 1 är en obligatorisk 3-timmarskurs om de vanligaste orsakerna till trafikolyckor: mörker, trötthet, hastighet och alkohol. Intyg utfärdas direkt.'
    : 'Riskutbildning Del 2 är en obligatorisk praktisk heldagsövning på halkbana. Du lär dig hantera kritiska situationer: halkkörning, nödbromsning och undvikningsmanövrar.';

  const riskItems = isR1
    ? [
        'Mörkers påverkan på trafiksäkerhet',
        'Trötthet och varningssignaler',
        'Hastighetens roll i olyckor',
        'Alkohol och droger',
        'Distraktioner – mobiltelefon',
        'Intyg utfärdas direkt',
      ]
    : [
        'Halkkörning och fordonsstabilitet',
        'Undvikningsmanövrar',
        'Nödbromsning',
        'Riskbedömning i svåra förhållanden',
        'Personlig handledning',
        'Intyg registreras hos Trafikverket',
      ];

  return (
    <div>
      <PageHero
        title="Riskutbildning"
        accent={`Del ${riskNum}`}
        desc={
          isR1
            ? 'Obligatorisk teori om mörker, trötthet och hastighet. 499 kr · 3 timmar.'
            : 'Obligatorisk praktisk halkbana. 1 990 kr · Heldag.'
        }
        breadcrumbs={[{ label: `Riskutbildning Del ${riskNum}` }]}
      />

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="font-display font-black text-2xl text-gray-900 uppercase mb-3 flex items-center gap-2">
                {isR1 ? (
                  <BookOpen size={18} className="text-red-600" />
                ) : (
                  <AlertTriangle size={18} className="text-red-600" />
                )}
                Om Riskutbildning Del {riskNum}
              </h2>

              <p className="text-gray-500 text-sm leading-relaxed mb-4">
                {riskDescription}
              </p>

              <ul className="space-y-2">
                {riskItems.map((t) => (
                  <li
                    key={t}
                    className="flex items-center gap-2 text-sm text-gray-600"
                  >
                    <CheckCircle size={12} className="text-red-500 shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                ['Pris', `${c.pris.toLocaleString('sv-SE')} kr`],
                ['Längd', c.duration],
                ['Plats', isR1 ? 'Kista, Stockholm' : 'Halkbana'],
                ['Intyg', 'Direkt'],
              ].map(([k, v]) => (
                <div
                  key={k}
                  className="bg-red-50 border border-red-100 rounded-xl p-4"
                >
                  <div className="text-xs text-red-400 font-bold uppercase tracking-wider mb-1">
                    {k}
                  </div>
                  <div className="font-display font-black text-xl text-red-600">
                    {v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            {done ? (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
                <div className="text-4xl mb-3">✅</div>
                <h3 className="font-display font-black text-2xl uppercase">
                  Bokning Bekräftad!
                </h3>
                <p className="text-gray-500 mt-2">Se dig på kursen!</p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl border-2 border-red-200 shadow-sm p-6"
              >
                <h2 className="font-display font-black text-xl text-gray-900 uppercase mb-4">
                  Boka Riskutbildning Del {riskNum}
                </h2>

                <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
                  Välj Kursdatum
                </div>

                {c.dates && c.dates.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2 mb-5">
                    {c.dates.map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setSelectedDate(d)}
                        className={`relative p-3 rounded-xl border-2 text-center transition-all ${
                          selectedDate === d
                            ? 'border-red-600 bg-red-50'
                            : 'border-gray-100 hover:border-red-200'
                        }`}
                      >
                        <div className="text-xs text-gray-400 font-bold uppercase">
                          {new Date(d + 'T12:00').toLocaleDateString('sv-SE', {
                            weekday: 'short',
                          })}
                        </div>
                        <div className="font-bold text-sm text-gray-900">
                          {new Date(d + 'T12:00').toLocaleDateString('sv-SE', {
                            day: 'numeric',
                            month: 'short',
                          })}
                        </div>

                        {selectedDate === d && (
                          <div className="absolute top-1.5 right-1.5 w-3.5 h-3.5 bg-red-600 rounded-full flex items-center justify-center text-white text-[9px] font-black">
                            ✓
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-400 mb-5 py-4 text-center">
                    Inga kommande datum tillgängliga online.
                    <br />
                    Ring oss för att boka!
                  </div>
                )}

                <div className="space-y-3 mb-4">
                  {[
                    ['name', 'Namn', 'text', 'Förnamn Efternamn'],
                    ['email', 'E-post', 'email', 'din@email.se'],
                    ['phone', 'Telefon', 'tel', '073-000 00 00'],
                  ].map(([k, l, t, ph]) => (
                    <div key={k}>
                      <label className="label">{l} *</label>
                      <input
                        type={t}
                        required
                        placeholder={ph}
                        value={form[k]}
                        onChange={(e) =>
                          setForm({ ...form, [k]: e.target.value })
                        }
                        className="input-field"
                      />
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">
                      Pris
                    </div>
                    <div className="font-display font-black text-2xl text-red-600">
                      {c.pris.toLocaleString('sv-SE')} kr
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || !selectedDate}
                    className="btn-red disabled:opacity-40"
                  >
                    {submitting ? (
                      'Bokar...'
                    ) : (
                      <>
                        <CheckCircle size={14} /> Boka Nu
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            <div className="bg-gray-900 rounded-2xl p-5 mt-4">
              <h3 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">
                Frågor? Ring oss
              </h3>
              <a
                href="tel:+46737547405"
                className="btn-red w-full justify-center mb-2"
              >
                <Phone size={13} /> Abubakar: 073-754 74 05
              </a>
              <a
                href="tel:+46704815743"
                className="btn-red w-full justify-center"
              >
                <Phone size={13} /> Ahmed: 070-481 57 43
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Risk1() {
  return <RiskPage courseId="risk-1" riskNum={1} />;
}

export function Risk2() {
  return <RiskPage courseId="risk-2" riskNum={2} />;
}

// ─── TIPS LIST ────────────────────────────────────────
const CAT_COLOR = {
  Guide: 'text-red-600 bg-red-50',
  Tips: 'text-orange-600 bg-orange-50',
  Planering: 'text-green-700 bg-green-50',
  Riskutbildning: 'text-blue-700 bg-blue-50',
};

export function Tips() {
  const { data: posts, loading } = useFetch(() => api.getPosts());

  return (
    <div>
      <PageHero
        title="Tips &"
        accent="Råd"
        desc="Expertguider och körkortstips från Alfa Trafikskola i Kista."
        breadcrumbs={[{ label: 'Tips' }]}
      />

      <div className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(posts || []).map((p) => (
              <Link
                key={p.slug}
                to={`/tips/${p.slug}`}
                className="card group hover:-translate-y-1 transition-all duration-200 flex flex-col overflow-hidden"
              >
                <div className="h-36 bg-gray-900 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-red-900/40 to-gray-900" />
                  <div
                    className="absolute inset-0 opacity-5"
                    style={{
                      backgroundImage:
                        'linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)',
                      backgroundSize: '24px 24px',
                    }}
                  />
                  <div className="absolute bottom-4 left-4">
                    <span
                      className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full ${
                        CAT_COLOR[p.category] || 'text-red-600 bg-red-50'
                      }`}
                    >
                      {p.category}
                    </span>
                  </div>
                </div>

                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock size={11} className="text-gray-400" />
                    <span className="text-xs text-gray-400">{p.readTime}</span>
                    <span className="text-gray-200">·</span>
                    <span className="text-xs text-gray-400">
                      {new Date(p.date).toLocaleDateString('sv-SE', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                  </div>

                  <h2 className="font-bold text-gray-900 mb-2 leading-snug group-hover:text-red-600 transition-colors">
                    {p.title}
                  </h2>

                  <p className="text-sm text-gray-500 leading-relaxed flex-1 line-clamp-3">
                    {p.intro}
                  </p>

                  <div className="flex items-center gap-1 mt-4 text-sm font-semibold text-red-600">
                    Läs mer
                    <ArrowRight
                      size={13}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TIPS POST ────────────────────────────────────────
export function TipsPost() {
  const { slug } = useParams();
  const { data: post, loading } = useFetch(() => api.getPost(slug), [slug]);
  const { data: allPosts } = useFetch(() => api.getPosts({ limit: 6 }));

  const related = (allPosts || [])
    .filter((p) => p.slug !== slug)
    .slice(0, 3);

  if (loading) {
    return (
      <div className="flex justify-center py-32">
        <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="text-center py-32">
        <h2 className="text-2xl font-bold">Artikel hittades inte</h2>
        <Link to="/tips" className="btn-red inline-flex mt-4">
          Tillbaka
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="relative bg-gray-900 pt-12 pb-10 overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-600" />

        <div className="relative max-w-4xl mx-auto px-6">
          <nav className="flex items-center gap-1.5 text-xs text-gray-500 mb-4">
            <Link to="/" className="hover:text-red-400">
              Hem
            </Link>
            <span>/</span>
            <Link to="/tips" className="hover:text-red-400">
              Tips
            </Link>
            <span>/</span>
            <span className="text-gray-400">{post.category}</span>
          </nav>

          <div
            className={`inline-flex text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full mb-3 ${
              CAT_COLOR[post.category] || 'text-red-600 bg-red-50'
            }`}
          >
            {post.category}
          </div>

          <h1 className="font-display font-black text-3xl md:text-5xl text-white uppercase leading-tight">
            {post.title}
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12 grid lg:grid-cols-[1fr_280px] gap-10 items-start">
        <article>
          <p className="text-lg text-gray-700 leading-relaxed mb-8 border-l-4 border-red-600 pl-4 italic">
            {post.intro}
          </p>

          {(post.sections || []).map((s, i) => (
            <section key={i} className="mb-8">
              <h2 className="font-display font-black text-2xl text-gray-900 uppercase mb-3">
                {s.heading}
              </h2>
              <p className="text-gray-600 leading-relaxed">{s.body}</p>
            </section>
          ))}

          <div className="flex flex-wrap items-center gap-2 py-4 border-t border-gray-100">
            <Tag size={12} className="text-gray-400" />
            {(post.tags || []).map((t) => (
              <span
                key={t}
                className="text-[10px] font-bold uppercase tracking-widest bg-red-50 text-red-600 px-2.5 py-1 rounded-full"
              >
                {t}
              </span>
            ))}
          </div>

          <div className="bg-red-600 rounded-2xl p-6 mt-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="font-display font-black text-xl text-white uppercase">
                Redo att börja köra?
              </div>
              <div className="text-red-100 text-sm mt-1">
                Boka hos Alfa Trafikskola i Kista.
              </div>
            </div>

            <Link
              to="/korlektioner"
              className="bg-white text-red-600 hover:bg-red-50 font-bold uppercase tracking-wide text-sm px-5 py-2.5 rounded inline-flex items-center gap-2 transition-colors"
            >
              Boka Nu <ArrowRight size={13} />
            </Link>
          </div>
        </article>

        <aside className="space-y-4 lg:sticky lg:top-28">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">
              Priser
            </h3>

            {[
              ['5 lektioner', '3 750 kr'],
              ['10 lektioner', '7 500 kr'],
              ['Risk 1', '499 kr'],
              ['Risk 2', '1 990 kr'],
              ['Teorilektion', '400 kr'],
            ].map(([l, p]) => (
              <div
                key={l}
                className="flex justify-between py-2 border-b border-gray-50 last:border-0 text-sm"
              >
                <span className="text-gray-500">{l}</span>
                <strong className="text-red-600 font-mono">{p}</strong>
              </div>
            ))}

            <Link to="/korlektioner" className="btn-red w-full justify-center mt-3 text-xs py-2">
              Boka Nu
            </Link>
          </div>

          {related.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">
                Fler Artiklar
              </h3>

              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/tips/${r.slug}`}
                  className="block py-2.5 border-b border-gray-50 last:border-0"
                >
                  <div className="text-[10px] font-bold uppercase tracking-wider text-red-500 mb-0.5">
                    {r.category}
                  </div>
                  <div className="text-sm font-medium text-gray-800 hover:text-red-600 transition-colors leading-snug">
                    {r.title}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

// ─── OM OSS ───────────────────────────────────────────
export function OmOss() {
  const { data: instructors } = useFetch(() => api.getInstructors());

  const inst =
    instructors || [
      {
        id: 1,
        name: 'Abubakar',
        role: 'Instruktör',
        phone: '+46 73 754 74 05',
        initials: 'AB',
      },
      {
        id: 2,
        name: 'Ahmed',
        role: 'Instruktör',
        phone: '+46 70 481 57 43',
        initials: 'AH',
      },
    ];

  return (
    <div>
      <PageHero
        title="Om"
        accent="Alfa Trafikskola"
        desc="Alfa Trafikskola AB i Kista. Org.nr 559470-5062. Nordkapsgatan 8 Tr, 164 36 Kista."
        breadcrumbs={[{ label: 'Om Oss' }]}
      />

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="font-display font-black text-2xl text-gray-900 uppercase mb-3">
                Vår Trafikskola
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">
                Alfa Trafikskola AB grundades med ett mål: erbjuda den bästa
                körkortsutbildningen i Kista och norra Stockholm. Vi är stolta
                över vår personliga service och höga godkännandegrad.
              </p>
              <p className="text-gray-500 text-sm leading-relaxed">
                Med instruktörerna Abubakar och Ahmed erbjuder vi
                körkortsutbildning på svenska och engelska.
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="font-display font-black text-2xl text-gray-900 uppercase mb-3">
                Företagsinformation
              </h2>

              <div className="space-y-2">
                {[
                  ['Företag', 'Alfa Trafikskola AB'],
                  ['Org.nr', '559470-5062'],
                  ['Adress', 'Nordkapsgatan 8 Tr, 164 36 Kista'],
                  ['E-post', 'info@alfatrafikskola.se'],
                  ['Telefon Abubakar', '+46 73 754 74 05'],
                  ['Telefon Ahmed', '+46 70 481 57 43'],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex gap-3 text-sm py-1.5 border-b border-gray-50 last:border-0"
                  >
                    <span className="text-gray-400 w-44 shrink-0">{k}</span>
                    <strong className="text-gray-900">{v}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="font-display font-black text-2xl text-gray-900 uppercase mb-4">
                Våra Instruktörer
              </h2>

              <div className="space-y-4">
                {inst.map((i) => (
                  <div
                    key={i.id}
                    className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl"
                  >
                    <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white font-black text-sm shrink-0">
                      {i.initials}
                    </div>

                    <div className="flex-1">
                      <div className="font-bold text-gray-900">{i.name}</div>
                      <div className="text-sm text-gray-500">{i.role}</div>
                      <a
                        href={`tel:${i.phone.replace(/\s/g, '')}`}
                        className="text-sm text-red-600 font-medium hover:underline"
                      >
                        {i.phone}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                ['2 000+', 'Nöjda elever'],
                ['94%', 'Godkänt direkt'],
                ['Kista', 'Nordkapsgatan 8'],
                ['559470-5062', 'Org.nr'],
              ].map(([v, l]) => (
                <div
                  key={l}
                  className="bg-red-50 border border-red-100 rounded-xl p-4"
                >
                  <div className="font-display font-black text-2xl text-red-600">
                    {v}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── KONTAKT ──────────────────────────────────────────
export function Kontakt() {
  const toast = useToast();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.sendContact(form);
      setSent(true);
      toast('Tack! Vi återkommer inom 1 arbetsdag.', 'success');
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHero
        title="Kontakta"
        accent="Oss"
        desc="Ring, maila eller fyll i formuläret – vi svarar snabbt!"
        breadcrumbs={[{ label: 'Kontakt' }]}
      />

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            ['📍', 'Adress', 'Nordkapsgatan 8 Tr\n164 36 Kista'],
            ['📞', 'Abubakar', '073-754 74 05'],
            ['📞', 'Ahmed', '070-481 57 43'],
            ['✉️', 'E-post', 'info@alfatrafikskola.se'],
          ].map(([icon, t, v]) => (
            <div
              key={t}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-center"
            >
              <div className="text-2xl mb-2">{icon}</div>
              <div className="font-bold text-gray-900 text-sm mb-1">{t}</div>
              <div className="text-xs text-gray-500 whitespace-pre-line">{v}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="font-display font-black text-2xl text-gray-900 uppercase mb-5">
              Skicka Meddelande
            </h2>

            {sent ? (
              <div className="text-center py-8">
                <CheckCircle size={48} className="text-green-500 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 text-lg mb-2">Tack!</h3>
                <p className="text-gray-500">
                  Vi återkommer inom 1 arbetsdag.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    ['name', 'Namn', 'text', 'Ditt namn'],
                    ['email', 'E-post', 'email', 'din@email.se'],
                    ['phone', 'Telefon', 'tel', '073-000 00 00'],
                    ['subject', 'Ämne', 'text', 'Vad gäller ärendet?'],
                  ].map(([k, l, t, ph]) => (
                    <div key={k}>
                      <label className="label">
                        {l}
                        {['name', 'email', 'subject'].includes(k) ? ' *' : ''}
                      </label>
                      <input
                        type={t}
                        required={['name', 'email', 'subject'].includes(k)}
                        placeholder={ph}
                        value={form[k]}
                        onChange={(e) =>
                          setForm({ ...form, [k]: e.target.value })
                        }
                        className="input-field"
                      />
                    </div>
                  ))}
                </div>

                <div>
                  <label className="label">Meddelande *</label>
                  <textarea
                    rows={5}
                    required
                    placeholder="Beskriv ditt ärende..."
                    value={form.message}
                    onChange={(e) =>
                      setForm({ ...form, message: e.target.value })
                    }
                    className="input-field resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-red disabled:opacity-40"
                >
                  {submitting ? 'Skickar...' : 'Skicka Meddelande'}
                </button>
              </form>
            )}
          </div>

          <div className="space-y-4">
            <div className="bg-gray-900 rounded-2xl p-6">
              <h3 className="font-bold text-white mb-4">Ring oss direkt</h3>

              <a
                href="tel:+46737547405"
                className="flex items-center gap-3 p-3 bg-white/5 hover:bg-red-600/20 rounded-xl transition-colors mb-2"
              >
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center text-white font-black text-sm">
                  AB
                </div>
                <div>
                  <div className="text-white font-bold text-sm">Abubakar</div>
                  <div className="text-gray-400 text-xs">
                    +46 73 754 74 05
                  </div>
                </div>
              </a>

              <a
                href="tel:+46704815743"
                className="flex items-center gap-3 p-3 bg-white/5 hover:bg-red-600/20 rounded-xl transition-colors"
              >
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center text-white font-black text-sm">
                  AH
                </div>
                <div>
                  <div className="text-white font-bold text-sm">Ahmed</div>
                  <div className="text-gray-400 text-xs">
                    +46 70 481 57 43
                  </div>
                </div>
              </a>
            </div>

            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 flex flex-col items-center justify-center text-center min-h-48">
              <div className="text-4xl mb-3">📍</div>
              <div className="font-bold text-gray-900">Nordkapsgatan 8 Tr</div>
              <div className="text-gray-500 text-sm">
                164 36 Kista, Stockholm
              </div>
              <a
                href="https://www.google.com/maps/search/Nordkapsgatan+8+Kista"
                target="_blank"
                rel="noreferrer"
                className="btn-red-sm mt-4 inline-flex"
              >
                Öppna i Maps
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── NOT FOUND ────────────────────────────────────────
export function NotFound() {
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center px-6 py-20 text-center bg-gray-50">
      <div className="font-display font-black text-[10rem] text-red-600/10 leading-none select-none">
        404
      </div>

      <div className="-mt-16 relative z-10">
        <h1 className="font-display font-black text-4xl text-gray-900 uppercase mb-3">
          Sidan hittades inte
        </h1>
        <p className="text-gray-500 max-w-md mb-6">
          Den sidan verkar inte finnas. Kör tillbaka till startsidan!
        </p>

        <div className="flex gap-3 justify-center flex-wrap">
          <Link to="/" className="btn-red">
            Hem
          </Link>
          <Link to="/korlektioner" className="btn-red-outline">
            Boka Lektion
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Risk1;